import { Container, Vector2 } from '../../../odie';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { getMatch2AssetPath } from '../../util/assets';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { IEffect } from './IEffect';
import { app } from '../../app';
import { mapGetPan } from '../../util/mapTools';
import { waitForItPromise } from 'src/lib/utils';
import { setBlockEntityViewLayer } from '../../entities/BlockEntity';
import { MovieClip } from '@play-co/timestep-core/ui';

// types
//-----------------------------------------------------------------------------
export type BombEffectOptions = {
  position: Vector2;
  radius: number;
  big: boolean;
};

/*
    bomb explosion effect
*/
export class BombEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: BombEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: BombEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  public static assets(_: Partial<BombEffectOptions>) {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    // animate
    await this._animate();

    // attack neighbors
    await this._attackNeighbors();
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _attackNeighbors() {
    const { radius, position } = this._options;

    const from = position.clone().subScalar(radius);
    const to = from.clone().addScalar(radius * 2 + 1);
    const map = this._scene.sessionEntity.c.map;
    const collisionTracker = getCollisionTracker();
    const area = new Vector2();

    // for each cell in affected area
    for (area.x = from.x; area.x < to.x; ++area.x) {
      for (area.y = from.y; area.y < to.y; ++area.y) {
        collisionTracker.collideAt(map, area, 'attack', { type: 'bomb' });
      }
    }
  }

  // private: animation
  //-------------------------------------------------------------------------
  private async _animate() {
    const { big, position } = this._options;

    const animation = new MovieClip({
      url: getMatch2AssetPath('blocks/power/bomb'),
      ...position,
      fps: 40,
    });

    // spawn animation as overlay entity
    const entity = spawnOverlayEntity(
      this._scene,
      position,
      { width: 1, height: 1 },
      () => new Container(animation),
    );

    setBlockEntityViewLayer(this._scene, entity, 'foreground');

    // Play big bomb spawn animation.
    if (big) {
      app.sound.play('puzzle-combo-bomb-bomb', {
        pan: mapGetPan(this._options.position),
      });
      await animation.playAsync('waterballoon_spawn_big');
    }

    // Scale a bit if it's a big bomb, since the animation is a bit smaller.
    animation.updateOpts({ scale: big ? 1.5 : 1 });
    // Play SFX.
    app.sound.play('puzzle-bomb-activate', {
      pan: mapGetPan(this._options.position),
    });

    // Play big bomb explode animation.
    animation
      .playAsync(big ? 'waterballoon_big_explode' : 'waterballoon_explode')
      .then(() => {
        despawnOverlayEntity(this._scene, entity);
      });

    await waitForItPromise(100);
  }
}
